/**
 * staffEmailStore — Staff email-delivery slice (Zustand).
 *
 *   GET  /api/staff/emails                 — list (page, limit, status, emailType)
 *   GET  /api/staff/emails/:id             — detail + attempt history
 *   GET  /api/staff/emails/failed          — failed/retrying, newest first
 *   POST /api/staff/emails/:id/retry       — requeue a failed delivery
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffEmailInitialState = {
  staffEmails: [],
  staffEmailsLoading: false,
  staffEmailsError: null,
  staffEmailsTotal: 0,
  staffEmailsPages: 1,

  staffEmailDetail: null,
  staffEmailDetailLoading: false,
  staffEmailDetailError: null,

  staffFailedEmails: [],
  staffFailedEmailsLoading: false,
  staffFailedEmailsError: null,
  staffFailedEmailsTotal: 0,
  staffFailedEmailsPages: 1,

  staffEmailMutationLoading: false,
  staffEmailMutationError: null,
};

export function createStaffEmailActions(set, get) {
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Failed to fetch emails");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  return {
    // GET /api/staff/emails
    fetchStaffEmails: (params = {}) => fetchList("staffEmails", "/api/staff/emails", params, ["emails"]),

    // GET /api/staff/emails/failed
    fetchStaffFailedEmails: (params = {}) =>
      fetchList("staffFailedEmails", "/api/staff/emails/failed", params, ["emails"]),

    // GET /api/staff/emails/:id
    fetchStaffEmail: async (id) => {
      set({ staffEmailDetailLoading: true, staffEmailDetailError: null });
      try {
        const data = await apiRequest(get, `/api/staff/emails/${id}`);
        const detail = data?.data ?? data?.email ?? data;
        set({ staffEmailDetailLoading: false, staffEmailDetail: detail });
        return { success: true, data: detail };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to fetch email job");
        set({ staffEmailDetailLoading: false, staffEmailDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // POST /api/staff/emails/:id/retry
    retryStaffEmail: async (id) => {
      set({ staffEmailMutationLoading: true, staffEmailMutationError: null });
      try {
        // POST /api/staff/emails/:id/retry takes no request body.
        const data = await apiRequest(get, `/api/staff/emails/${id}/retry`, {
          method: "POST",
        });
        set({
          staffEmailMutationLoading: false,
          staffEmailMutationError: null,
          successMessage: "Email queued for retry",
        });
        return { success: true, data: data?.data ?? data };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to retry email");
        set({ staffEmailMutationLoading: false, staffEmailMutationError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const staffEmailActions = createStaffEmailActions;
export default staffEmailActions;
